/* tslint:disable:import-spacing no-inferrable-types variable-name align no-trailing-whitespace */

import {DecisionPoint} from './decision.point';
import {InterLink}     from './inter.link';

/** @enum TrackType the kinds of track */
export enum TrackType {
  MAIN     = 0,
  PARKING  = 1,
  TERMINAL = 2,
  JUNCTION = 3,
  UNKNOWN  = 4
}

/**
 *
 * @class Track
 *
 * @brief a physical track (possibly one of many) between two decision points.  the bundle
 * of all the tracks between a decision point pair is a InterLink.  A string of InterLinks
 * forms a subdivision, bounded by terminal links.  A string subdivisions forms a complete
 * rail corridor.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2019
 *
 */
export class Track {

  /** @var number unique id for this track */
  public            _id: number              = 0;

  /** @var number the actual mileage */
  public    _physLength: number              = 0;

  /** @var number the usable mileage */
  public     _useLength: number              = 0;

  /** @var number maximum weight (short tons) for this track */
  public     _maxWeight: number              = 0;

  /** @var number the track number in this container InterLink */
  public         _track: number              = 0;
  public     _origTrack: number              = 0;

  /** @var boolean flag for being south or north track (for double track only) */
  public    _southTrack: boolean             = false;

  /** @var TrackType the kind of track */
  public          _type: TrackType           = TrackType.MAIN;

  /** @var Map<number, number> = new Map<number, number> the mapping of train class to minimum run time for this track */
  public           _mrt: Map<number, number> = new Map<number, number>();

  /** @var number the 0 based index of this track */
  public           _nth: number              = 0;

  /** @var DecisionPoint|null decision point before this track */
  public         _after: DecisionPoint|null  = null;

  /* @var DecisionPoint|null decision point after this track */
  public        _before: DecisionPoint|null  = null;

  /** @var InterLink|null mileage grouped tracks between the before/after decision points */
  public         _peers: InterLink|null      = null;

  /** @brief standard constructor */
  constructor( id: number,
                p: number,
                u: number,
                m: number,
              trk: number,
              typ: string,
              nth: number) {

    this._id         = id;
    this._physLength = p;
    this._useLength  = u;
    this._maxWeight  = m;
    this._track      = trk;
    this._origTrack  = trk;
    this._type       = Track.typeFromString(typ);
    this._nth        = nth;

  }

  /** helper to fetch the miles between this the decision points on either side */
  public miles(): number {

    if ((this._after === null) || (this._before === null)) {

      return 0;
    }

    return Math.abs(this._after._mileage - this._before._mileage);
  }

  /** max mileage (decision point) of either end of track */
  public maxMiles(): number {

    let dm: number|null = null;
    let sm: number|null = null;

    if (this._before !== null) {

      sm = this._before._mileage;
    }

    if (this._after !== null) {

      dm = this._after._mileage;
    }

    return Math.max(...([sm, dm].filter((x: number|null): boolean  =>  {
      return x !== null;
    })) as number[]);
  }

  /** min mileage (decision point) of either end of track */
  public minMiles(): number {

    let dm: number|null = null;
    let sm: number|null = null;

    if (this._before !== null) {

      sm = this._before._mileage;
    }

    if (this._after !== null) {

      dm = this._after._mileage;
    }

    return Math.min(...([sm, dm].filter((x:number|null): boolean => {
      return x !== null;
    })) as number[]);
  }

  /** @brief convert track type to a string */
  public static typeToString(type: TrackType): string {

    switch (type) {
      case TrackType.MAIN:     return 'M';
      case TrackType.PARKING:  return 'P';
      case TrackType.JUNCTION: return 'J';
      case TrackType.TERMINAL: return 'T';
      case TrackType.UNKNOWN:  return 'U';
    }

    return '';
  }

  /** @brief convert track string to a type */
  public static typeFromString(type: string): TrackType {

    const tmp: string = type.toLowerCase().trim();

    if (tmp === 't') {

      return TrackType.TERMINAL;

    } else if (tmp === 'j') {

      return TrackType.JUNCTION;

    } else if (tmp === 'p') {

      return TrackType.PARKING;

    } else if (tmp === 'u') {

      return TrackType.UNKNOWN;
    }

    return TrackType.MAIN;
  }
}
